import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import ContentLoader from 'react-content-loader';
import {GlossaryErrorState} from '@learningblocks/shared-components';
import style from './style.module.scss';

/**
 * GlossaryListBlock
 *  - Component determines what to display on the listing block
 *  - Component reacts to changes in listApi and render Loadin, Error and Data Pages.
 */
export function GlossaryListBlock(props) {
  const renderGlossaryContent = () => {
    if (props.isEmpty) {
      return <GlossaryListingEmpty linkURL="https://www.labxchange.org/library/"></GlossaryListingEmpty>;
    } else {
      return props.children;
    }
  };

  if (props.loading === false && props.error === false) {
    return renderGlossaryContent();
  } else if (props.error) {
    return <GlossaryListingError onRetry={props.onRetry} />;
  } else {
    return <GlossaryListLoading />;
  }
}

GlossaryListBlock.propTypes = {
  /**
   * Props of child components for the block
   */
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element])
    .isRequired,
  /**
   * Prop if list is loading
   */
  loading: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.bool]),
  /**
   * Prop if fetchingApi returned error
   */
  error: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.bool]),
  /**
   * Boolean prop specifying if GlossaryList isEmpty
   */
  isEmpty: PropTypes.bool.isRequired,
  /**
   * Function to execute when retry button is clicked
   */
  onRetry: PropTypes.func.isRequired,
};

GlossaryListBlock.defaultProps = {
  loading: null,
  error: null,
  isEmpty: true,
};

/**
 * GlossaryListingError
 *  - Display Error message when glossary listing api fails
 */
function GlossaryListingError({onRetry}) {
  return (
    <GlossaryErrorState
      onRetry={onRetry}
      className={style.glossaryListingErrorBlock}
    />
  );
}

GlossaryListingError.propTypes = {
  /**
   * Function to execute when retry button is clicked
   */
  onRetry: PropTypes.func.isRequired,
};

/**
 * GlossaryListLoading
 *  - Display loader when glossary listing api is loading
 */
function GlossaryListLoading() {
  return (
    <ContentLoader
      speed={2}
      className={style.glossaryListingLoadingBlock}
      viewBox='0 0 334 540'
      backgroundColor='#f3f3f3'
      foregroundColor='#ecebeb'>
      <path d='M 0 0 h 334 v 16 H 0 V 0 z m 0 280 h 334 v 16 H 0 v -16 z M 295 40 H 0 v 16 h 295 V 40 z M 0 320 h 295 v 16 H 0 v -16 z m 295 240 H 0 v 16 h 295 v -16 z M 0 80 h 178 v 16 H 0 V 80 z m 178 280 H 0 v 16 h 178 v -16 z M 0 600 h 178 v 16 H 0 v -16 z m 295 -480 H 0 v 16 h 295 v -16 z M 0 400 h 295 v 16 H 0 v -16 z m 295 240 H 0 v 16 h 295 v -16 z M 0 160 h 334 v 16 H 0 v -16 z m 334 280 H 0 v 16 h 334 v -16 z M 0 680 h 334 v 16 H 0 v -16 z m 239 -480 H 0 v 16 h 239 v -16 z M 0 480 h 239 v 16 H 0 v -16 z m 239 240 H 0 v 16 h 239 v -16 z M 0 240 h 295 v 16 H 0 v -16 z m 295 280 H 0 v 16 h 295 v -16 z' />
    </ContentLoader>
  );
}

/**
 * GlossaryListingEmpty
 *  - Display empty state for Glossary Items listing
 */

function GlossaryListingEmpty(props) {
  return (
    <div className={style.glossaryListingEmptyBlock}>
      <h3><ReactMarkdown>{props.title}</ReactMarkdown></h3>
      <p><ReactMarkdown>{props.description}</ReactMarkdown></p>
      <a
        href={props.linkURL}
        alt={props.linkTitle}
        target='_blank'
        rel='noreferrer noopener'>
        {props.linkTitle}
      </a>
    </div>
  );
}

GlossaryListingEmpty.propTypes = {
  /**
   * Main title to show in the glossary component
   */
  title: PropTypes.string.isRequired,
  /**
   * Detailed messge to show on Empty state window
   */
  description: PropTypes.string.isRequired,
  /**
   * Title for the further reading link displayed in bottom
   */
  linkTitle: PropTypes.string.isRequired,
  /**
   * Url to the further reading link in the botton
   */
  linkURL: PropTypes.string.isRequired,
};

GlossaryListingEmpty.defaultProps = {
  title: 'Sorry, no results found',
  description: 'Try a different search term or category.',
  linkTitle: 'Go to LabXchange library.',
  linkURL: 'https://www.labxchange.org/library/',
};
